import { NotifyErrorWrapper, NotifySuccess } from '@/helpers/core/notifications'
import {
  bulkDeleteDowntimes,
  createDowntime,
  getClientsDowntimeSummary,
  getGroupedDowntimes,
  getMinersDowntimeSummary,
  getRacksDowntimeSummary,
  getSitesDowntimeSummary,
  getStructuresDowntimeSummary,
  removeMinersFromDowntime,
} from '@/services/eos/downtimes'
import type {
  IClientDowntimeSummaryFilters,
  IDowntimeFilters,
  IInfrastructureDowntimeSummaryFilters,
  IMinersDowntimeSummaryFilters,
} from '@/types/eos/filters'
import type { IDowntimeCreationForm } from '@/types/eos/forms'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'

export const useDowntimes = () => {
  const client = useQueryClient()

  const getGroupedDowntimesQuery = (filters: IDowntimeFilters) =>
    useQuery({
      queryKey: ['grouped-downtimes', filters],
      queryFn: () => getGroupedDowntimes(filters),
    })

  const getClientsDowntimeSummaryQuery = (filters: IClientDowntimeSummaryFilters) =>
    useQuery({
      queryKey: ['clients-downtime-summary', filters],
      queryFn: () => getClientsDowntimeSummary(filters),
    })

  const getSitesDowntimeSummaryQuery = (filters: IInfrastructureDowntimeSummaryFilters) =>
    useQuery({
      queryKey: ['sites-downtime-summary', filters],
      queryFn: () => getSitesDowntimeSummary(filters),
    })

  const getStructuresDowntimeSummaryQuery = (filters: IInfrastructureDowntimeSummaryFilters) =>
    useQuery({
      queryKey: ['structures-downtime-summary', filters],
      queryFn: () => getStructuresDowntimeSummary(filters),
    })

  const getRacksDowntimeSummaryQuery = (filters: IInfrastructureDowntimeSummaryFilters) =>
    useQuery({
      queryKey: ['racks-downtime-summary', filters],
      queryFn: () => getRacksDowntimeSummary(filters),
    })

  const getMinersDowntimeSummaryQuery = (filters: IMinersDowntimeSummaryFilters) =>
    useQuery({
      queryKey: ['miners-downtime-summary', filters],
      queryFn: () => getMinersDowntimeSummary(filters),
    })

  const bulkDeleteDowntimesMutation = useMutation({
    mutationFn: (ids: number[]) => bulkDeleteDowntimes(ids),
    onError: NotifyErrorWrapper('Failed to delete downtime'),
    onSuccess: () => {
      invalidateSummaryQueries()
      NotifySuccess(`Downtime deleted successfully`)
    },
  })

  const createDowntimeMutation = useMutation({
    mutationFn: (form: IDowntimeCreationForm) => createDowntime(form),
    onError: NotifyErrorWrapper('Failed to create downtime'),
    onSuccess: () => {
      invalidateSummaryQueries()
      NotifySuccess(`Downtime created successfully`)
    },
  })

  const removeMinersFromDowntimeMutation = useMutation({
    mutationFn: ({ downtimeID, minerIDs }: { downtimeID: number; minerIDs: string[] }) =>
      removeMinersFromDowntime(downtimeID, minerIDs),
    onError: NotifyErrorWrapper('Failed to remove miners from downtime'),
    onSuccess: () => {
      invalidateSummaryQueries()
      NotifySuccess(`Miners removed from downtime successfully`)
    },
  })

  const invalidateSummaryQueries = () => {
    client.invalidateQueries({ queryKey: ['grouped-downtimes'] })
    client.invalidateQueries({ queryKey: ['clients-downtime-summary'] })
    client.invalidateQueries({ queryKey: ['sites-downtime-summary'] })
    client.invalidateQueries({ queryKey: ['structures-downtime-summary'] })
    client.invalidateQueries({ queryKey: ['racks-downtime-summary'] })
    client.invalidateQueries({ queryKey: ['miners-downtime-summary'] })
  }

  return {
    getGroupedDowntimesQuery,
    getClientsDowntimeSummaryQuery,
    getSitesDowntimeSummaryQuery,
    getStructuresDowntimeSummaryQuery,
    getRacksDowntimeSummaryQuery,
    getMinersDowntimeSummaryQuery,
    createDowntimeMutation,
    bulkDeleteDowntimesMutation,
    removeMinersFromDowntimeMutation,
  }
}
